import { computerCommandMenuItemSelectedEvent, getComputerMenuItemSelectedValue, } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/commandMenuEvents.js';
import { dispatchComputerCommand } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/computerCommandPolicy.js';
import { isComputerMenuItemAllowed } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/computerMenuModel.js';
import { runAfterCurrentSelectionEvent, useComputerCommandMenuActions, } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useComputerCommandMenuActions.js';
import { useGlobalAvailableSources } from '#pplx/frontend/apps/ask/hooks/shared-source-selection-hooks/useSharedSourceSelection.js';
import { useSourceActivity } from '#pplx/frontend/apps/ask/user-settings/hooks/useSourceActivity.js';
import { useCallback } from 'react';
/** Shares the catalog selection flow while leaving editor operations with the owning input. */
export function useComputerCommandMenuSelection({ getEditor, skillsProps, onMenuClose, openModal, isIncognito, onUiActionSelect, onOpenSettings, isFollowUp, isAlwaysIncognitoRequired, resolvedSearchQuery, track, }) {
    const { sources: selectedSourceIds, setSources } = useGlobalAvailableSources();
    const { trackSourceActivity } = useSourceActivity();
    const { handleSlashCommandSelect, handleUiActionSelect } = useComputerCommandMenuActions({
        getEditor,
        onMenuClose,
        openModal,
        isIncognito,
        onUiActionSelect,
        onOpenSettings,
    });
    const handleSkillSelect = useCallback((skill) => {
        if (!skillsProps.showSkills)
            return;
        runAfterCurrentSelectionEvent(() => {
            skillsProps.onSkillSelect(skill);
            onMenuClose();
        });
    }, [onMenuClose, skillsProps]);
    const handlePluginSelect = useCallback((plugin) => {
        const editor = getEditor();
        if (!editor)
            return;
        runAfterCurrentSelectionEvent(() => {
            editor.insertPluginMention({
                id: plugin.action.plugin_id,
                name: plugin.action.plugin_name,
                displayName: plugin.title,
            }, { prefixWithSlash: false, replaceActiveCommandQuery: true });
            onMenuClose();
        });
    }, [onMenuClose, getEditor]);
    const handleItemSelect = useCallback((item, selectionMethod) => {
        if (!isComputerMenuItemAllowed(item, isFollowUp, isAlwaysIncognitoRequired))
            return;
        if (selectionMethod !== 'button') {
            track(computerCommandMenuItemSelectedEvent({
                name: 'computer command menu item selected',
                data: {
                    item: item.action.type,
                    value: getComputerMenuItemSelectedValue(item),
                    section: item.type,
                    selection_method: selectionMethod,
                    has_search_query: resolvedSearchQuery.length > 0,
                },
            }));
        }
        if (item.action.type === 'connector') {
            const editor = getEditor();
            if (!editor) {
                return;
            }
            const source = item.action.source;
            runAfterCurrentSelectionEvent(() => {
                if (!selectedSourceIds.includes(source.id)) {
                    setSources([...selectedSourceIds, source.id]);
                }
                trackSourceActivity(source.id);
                editor.insertMention({
                    uuid: source.id,
                    variant: 'sources',
                    text: source.display_name || source.id,
                    icon: { type: 'source', sourceId: source.id },
                }, { replaceActiveCommandQuery: true });
                onMenuClose();
            });
            return;
        }
        if (item.action.type === 'skill') {
            handleSkillSelect(item.action.selectableSkill);
            return;
        }
        if (item.action.type === 'plugin') {
            handlePluginSelect(item.action.plugin);
            return;
        }
        dispatchComputerCommand({
            action: item.action,
            icon: item.icon,
            onSlashCommandSelect: handleSlashCommandSelect,
            onUiActionSelect: handleUiActionSelect,
        });
    }, [
        getEditor,
        onMenuClose,
        selectedSourceIds,
        setSources,
        trackSourceActivity,
        handlePluginSelect,
        handleSkillSelect,
        handleSlashCommandSelect,
        handleUiActionSelect,
        isAlwaysIncognitoRequired,
        isFollowUp,
        resolvedSearchQuery,
        track,
    ]);
    const getUseButtonTrack = useCallback((item) => computerCommandMenuItemSelectedEvent({
        name: 'computer command menu item selected',
        data: {
            item: item.action.type,
            value: getComputerMenuItemSelectedValue(item),
            section: item.type,
            selection_method: 'button',
            has_search_query: resolvedSearchQuery.length > 0,
        },
    }), [resolvedSearchQuery]);
    return { handleItemSelect, getUseButtonTrack };
}
//# sourceMappingURL=useComputerCommandMenuSelection.js.map